import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { esignApiService, type ESignSession, type ESignSignerField } from '../services/esignApiService'
import { ApiError } from '../services/BaseApiService'
import { Button, Modal, Textarea, useDialogs } from '../components/ui'
import PdfPages, { type PdfPageInfo } from '../components/pdf/PdfPages'
import AdoptSignatureModal, { type AdoptedSignature } from '../components/esign/AdoptSignatureModal'
import { pngSrc } from '../components/esign/signatureImage'
import { downloadBlob, errorMessage, FIELD_LABELS, fmtDate } from '../components/esign/esignShared'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'

type LoadState = { kind: 'loading' } | { kind: 'invalid' } | { kind: 'error'; message: string } | { kind: 'ready'; session: ESignSession }

const FIELD_HIGHLIGHT = '#F59E0B'

export default function ESignPage() {
  const dialogs = useDialogs()
  const { token = '' } = useParams<{ token: string }>()
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' })
  const [doc, setDoc] = useState<Blob | null>(null)
  const [docError, setDocError] = useState('')
  const [consent, setConsent] = useState(false)
  const [adopted, setAdopted] = useState<AdoptedSignature | null>(null)
  const [adoptOpen, setAdoptOpen] = useState(false)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState('')
  const [outcome, setOutcome] = useState<'signed' | 'declined' | null>(null)
  const [downloading, setDownloading] = useState(false)

  const loadSession = useCallback(async () => {
    try {
      const session = await esignApiService.session(token)
      setLoad({ kind: 'ready', session })
      return session
    } catch (err) {
      setLoad(err instanceof ApiError && (err.status === 404 || err.status === 400)
        ? { kind: 'invalid' }
        : { kind: 'error', message: errorMessage(err, 'We could not load this signing request.') })
      return null
    }
  }, [token])

  useEffect(() => { loadSession() }, [loadSession])

  const session = load.kind === 'ready' ? load.session : null
  const signing = !!session && !outcome && session.signer.status === 'pending' && session.request.status === 'pending' && session.yourTurn

  useEffect(() => {
    if (!signing || doc) return
    esignApiService.document(token)
      .then(setDoc)
      .catch(err => setDocError(errorMessage(err, 'The document could not be loaded.')))
  }, [signing, doc, token])

  async function finish() {
    if (!adopted || !consent) return
    setBusy(true)
    setActionError('')
    try {
      await esignApiService.sign({
        token,
        consent: true,
        signatureText: adopted.signatureText,
        signatureImagePng: adopted.signaturePng,
        ...(adopted.initialsPng ? { initialsImagePng: adopted.initialsPng } : {}),
      })
      setOutcome('signed')
      await loadSession()
    } catch (err) {
      setActionError(errorMessage(err, 'Your signature could not be recorded.'))
    } finally {
      setBusy(false)
    }
  }

  async function decline() {
    if (!declineReason.trim()) return
    setBusy(true)
    setActionError('')
    try {
      await esignApiService.decline(token, declineReason.trim())
      setDeclineOpen(false)
      setOutcome('declined')
    } catch (err) {
      setActionError(errorMessage(err, 'Your response could not be recorded.'))
    } finally {
      setBusy(false)
    }
  }

  async function downloadSigned() {
    setDownloading(true)
    try {
      const blob = await esignApiService.signedDocument(token)
      downloadBlob(blob, `${(session?.title || 'signed-document').replace(/[^\w.-]+/g, '_')}.pdf`)
    } catch (err) {
      void dialogs.alert(errorMessage(err, 'The signed document is not available yet.'))
    } finally {
      setDownloading(false)
    }
  }

  if (load.kind === 'loading') return <Shell><p style={{ color: colors.textSecondary }}>Loading…</p></Shell>
  if (load.kind === 'invalid') {
    return <Notice title="This signing link is not valid" body="The link may be mistyped or no longer in use. Check the email you received, or contact the sender." />
  }
  if (load.kind === 'error') return <Notice title="Something went wrong" body={load.message} />

  const s = load.session
  const downloadBtn = s.signedDocumentAvailable && (
    <Button size="lg" onClick={downloadSigned} loading={downloading} style={{ marginTop: spacing.lg }}>Download signed document</Button>
  )

  if (outcome === 'signed') {
    return (
      <Notice
        icon="✓"
        title="You're done"
        body={s.signedDocumentAvailable
          ? 'Everyone has signed. A copy of the completed document has been emailed to all parties.'
          : 'Your signature has been recorded. A copy of the completed document will be emailed to you when everyone has signed.'}
      >
        {downloadBtn}
      </Notice>
    )
  }
  if (outcome === 'declined' || s.signer.status === 'declined') {
    return <Notice title="You declined to sign" body="The sender has been notified. No further action is needed." />
  }
  if (s.signer.status === 'signed') {
    return (
      <Notice
        icon="✓"
        title="You've already signed"
        body={s.signedDocumentAvailable
          ? 'Everyone has signed this document.'
          : 'Your signature is recorded. A copy of the completed document will be emailed to you when everyone has signed.'}
      >
        {downloadBtn}
      </Notice>
    )
  }
  if (s.request.status === 'expired') {
    return <Notice title="This signing request has expired" body={`It expired${s.request.expiresAt ? ` on ${fmtDate(s.request.expiresAt)}` : ''}. Contact ${s.requesterName || 'the sender'} if you still need to sign.`} />
  }
  if (s.request.status === 'voided') {
    return <Notice title="This signing request was cancelled" body={`${s.requesterName || 'The sender'} voided this request, so it can no longer be signed.`} />
  }
  if (s.request.status === 'declined') {
    return <Notice title="This signing request was declined" body="Another signer declined, so the request is closed." />
  }
  if (s.request.status === 'completed') {
    return <Notice icon="✓" title="This document is complete" body="Everyone has signed.">{downloadBtn}</Notice>
  }
  if (!s.yourTurn) {
    return (
      <Notice
        title="It's not your turn yet"
        body="This document is being signed in order. You'll get an email when it's your turn to sign."
      />
    )
  }

  const needsInitials = s.fields.some(f => f.kind === 'initials')
  const signerName = s.signer.name || ''

  function fieldContent(f: ESignSignerField, scale: number) {
    if (!adopted) return <span style={{ fontSize: Math.max(9, Math.min(14, f.height * scale * 0.45)) }}>{FIELD_LABELS[f.kind]}</span>
    const textSize = Math.max(8, f.height * scale * 0.6)
    switch (f.kind) {
      case 'signature':
        return <img src={pngSrc(adopted.signaturePng)} alt="Your signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      case 'initials':
        return adopted.initialsPng
          ? <img src={pngSrc(adopted.initialsPng)} alt="Your initials" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : null
      case 'date':
        return <span style={{ fontSize: textSize, color: colors.text }}>{new Date().toLocaleDateString()}</span>
      case 'name':
        return <span style={{ fontSize: textSize, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden' }}>{adopted.signatureText}</span>
    }
  }

  const overlay = ({ pageIndex, scale }: PdfPageInfo) => s.fields.filter(f => f.page === pageIndex).map(f => (
    <button
      key={f.fieldId}
      type="button"
      data-testid={`field-${f.fieldId}`}
      aria-label={`${FIELD_LABELS[f.kind]} field`}
      onClick={() => !adopted && setAdoptOpen(true)}
      style={{
        position: 'absolute',
        left: f.x * scale, top: f.y * scale, width: f.width * scale, height: f.height * scale,
        boxSizing: 'border-box', padding: 1,
        display: 'flex', alignItems: 'center', justifyContent: adopted && f.kind !== 'signature' && f.kind !== 'initials' ? 'flex-start' : 'center',
        overflow: 'hidden',
        background: adopted ? 'transparent' : 'rgba(245,158,11,0.18)',
        border: adopted ? '1px dashed rgba(245,158,11,0.6)' : `2px solid ${FIELD_HIGHLIGHT}`,
        borderRadius: 3,
        color: '#92400E', fontWeight: 600, fontFamily: fontFamily.sans,
        cursor: adopted ? 'default' : 'pointer',
      }}
    >
      {fieldContent(f, scale)}
    </button>
  ))

  return (
    <>
    <div style={{ minHeight: '100vh', background: colors.background, overflowX: 'hidden' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: `${spacing.lg} ${spacing.md} 120px`, boxSizing: 'border-box' }}>
        <Brand />
        <section style={card}>
          {s.requesterName && (
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>
              {s.requesterName} has requested your signature
            </div>
          )}
          <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0, overflowWrap: 'anywhere' }}>
            {s.title || 'Document for signature'}
          </h1>
          {s.message && <p style={{ color: colors.textBody, fontSize: fontSize.base, margin: `${spacing.sm} 0 0`, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{s.message}</p>}
          <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.sm }}>
            Signing as {signerName ? `${signerName} · ` : ''}{s.signer.email}
            {s.request.expiresAt && ` · Expires ${fmtDate(s.request.expiresAt)}`}
          </div>
        </section>

        <section style={card}>
          <h2 style={{ fontSize: fontSize.md, fontWeight: 600, color: colors.text, margin: `0 0 ${spacing.sm}` }}>Electronic records and signatures</h2>
          <div style={{
            maxHeight: 180, overflowY: 'auto', fontSize: fontSize.sm, color: colors.textBody, lineHeight: 1.55,
            whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: colors.background,
            border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm,
          }}>
            {s.disclosure}
          </div>
          <label style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start', marginTop: spacing.md, fontSize: fontSize.base, color: colors.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
            <span>I have read the disclosure above and agree to use electronic records and signatures.</span>
          </label>
        </section>

        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, margin: `0 0 ${spacing.sm}` }}>
          {s.fields.length > 0
            ? `${s.fields.length} field${s.fields.length === 1 ? '' : 's'} highlighted for you. Adopt your signature to fill them in.`
            : 'Your signature will be recorded on the completion certificate attached to this document.'}
        </div>

        {docError
          ? <div style={{ ...card, color: colors.error, fontSize: fontSize.sm }}>{docError}</div>
          : <PdfPages file={doc} renderOverlay={overlay} onError={setDocError} />}
      </div>

      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
        background: colors.surface, borderTop: `1px solid ${colors.border}`, boxShadow: shadow.lg,
        padding: `${spacing.sm} ${spacing.md}`, paddingBottom: `max(${spacing.sm}, env(safe-area-inset-bottom))`,
      }}>
        <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: spacing.sm }}>
          <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: fontSize.xs, color: actionError ? colors.error : colors.textSecondary }}>
            {actionError || (!adopted ? 'Adopt your signature to continue.' : !consent ? 'Agree to the electronic records disclosure to finish.' : 'Ready to finish.')}
          </div>
          <Button variant="danger" onClick={() => { setDeclineReason(''); setActionError(''); setDeclineOpen(true) }} disabled={busy}>Decline</Button>
          {adopted && <Button variant="secondary" onClick={() => setAdoptOpen(true)} disabled={busy}>Change signature</Button>}
          {adopted
            ? <Button onClick={finish} disabled={!consent} loading={busy}>Finish signing</Button>
            : <Button onClick={() => setAdoptOpen(true)}>Adopt signature</Button>}
        </div>
      </div>

      {adoptOpen && (
        <AdoptSignatureModal
          open
          defaultName={adopted?.signatureText ?? signerName}
          needsInitials={needsInitials}
          onClose={() => setAdoptOpen(false)}
          onAdopt={a => { setAdopted(a); setAdoptOpen(false) }}
        />
      )}

      <Modal
        open={declineOpen}
        onClose={() => !busy && setDeclineOpen(false)}
        title="Decline to sign?"
        width={460}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeclineOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={decline} disabled={!declineReason.trim()} loading={busy}>Decline to sign</Button>
          </>
        }
      >
        <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, margin: `0 0 ${spacing.md}` }}>
          The sender will be notified and the request will be closed. This cannot be undone.
        </p>
        <Textarea label="Reason" value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={4} maxLength={1000} />
        {actionError && <div style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.sm }}>{actionError}</div>}
      </Modal>
    </div>
      {dialogs.element}
    </>
  )
}

const card: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
  padding: spacing.md, marginBottom: spacing.md, boxShadow: shadow.xs,
}

function Brand() {
  return <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.primary, marginBottom: spacing.md }}>DocMorphia</div>
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: colors.background, overflowX: 'hidden' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: `${spacing.xl} ${spacing.md}`, boxSizing: 'border-box' }}>
        <Brand />
        {children}
      </div>
    </div>
  )
}

function Notice({ title, body, icon, children }: { title: string; body: string; icon?: string; children?: React.ReactNode }) {
  return (
    <Shell>
      <div style={{ ...card, padding: spacing.lg, textAlign: 'center' }}>
        {icon && <div style={{ fontSize: 40, color: colors.success, marginBottom: spacing.sm }}>{icon}</div>}
        <h1 style={{ fontSize: fontSize.xl, fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0 }}>{title}</h1>
        <p style={{ color: colors.textSecondary, fontSize: fontSize.base, margin: `${spacing.sm} 0 0`, lineHeight: 1.5 }}>{body}</p>
        {children}
      </div>
    </Shell>
  )
}
